import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toast } from "react-toastify";
import {
  cartService,
  CartAddPreflightResult,
  CartAddResult,
  CartItemUpdate,
  ManualCartItem,
  MAX_CART_ITEMS,
} from "../services/cart.service";
import { cartApiService } from "../services/cart-api.service";
import { Product, BusinessInfo } from "../services/barcode.service";
import { authService } from "../services/auth.service";
import { translationService } from "../services/translation.service";

interface CartContextType {
  items: ManualCartItem[];
  itemCount: number;
  maxCartItems: number;
  remainingCartSlots: number;
  isCartFull: boolean;
  isCartLoading: boolean;

  addScannedProduct: (
    product: Product,
    price: string,
    availability: "TRUE" | "FALSE",
    rowIndex: number,
    notes?: string,
    catalogAvgWeight?: string,
  ) => Promise<CartAddResult>;
  addManualProduct: (
    data: {
      name: string;
      barcode?: string;
      category?: string;
      subCategory?: string;
      categoryNames?: { ar?: string; he?: string; en?: string };
      subCategoryNames?: { ar?: string; he?: string; en?: string };
      imageURL?: string;
      quantityType?: string;
      avgWeight?: string;
      weightToPresent?: string;
    },
    price: string,
    availability: "TRUE" | "FALSE",
    notes?: string,
  ) => Promise<CartAddResult>;
  preflightAddByBarcode: (barcode?: string) => CartAddPreflightResult;
  removeItem: (itemId: string) => Promise<void>;
  updateItem: (itemId: string, updates: CartItemUpdate) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  submitCart: () => Promise<{
    ok: boolean;
    message: string;
    count: number;
    duplicateSkippedCount: number;
  }>;

  getItemName: (item: ManualCartItem) => string;
  getItemImage: (item: ManualCartItem) => string | undefined;
  getItemBarcode: (item: ManualCartItem) => string | undefined;

  businessInfo: BusinessInfo | null;
  setBusinessInfo: (info: BusinessInfo) => void;
  clearBusinessInfo: () => void;
  otpSending: boolean;
  otpVerifying: boolean;
  otpLocked: boolean;
  otpReady: boolean;
  isAuthInitializing: boolean;
  otpLockRemainingSeconds: number;
  setOtpSending: (value: boolean) => void;
  setOtpVerifying: (value: boolean) => void;
  setOtpLockedState: (locked: boolean, lockRemainingSeconds?: number) => void;
  completeOtpAuthentication: (info: BusinessInfo) => void;
  logoutOtpSession: () => void;

  lastAddedItem: ManualCartItem | null;
  clearLastAddedItem: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function normalizeServerItem(raw: ManualCartItem): ManualCartItem {
  return {
    ...raw,
    id: String(raw.id),
    availability: raw.availability === "FALSE" ? "FALSE" : "TRUE",
    notes: raw.notes || "",
    addedAt: Number(raw.addedAt) || Date.now(),
  };
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<ManualCartItem[]>([]);
  const [businessInfo, setBusinessInfoState] = useState<BusinessInfo | null>(
    null,
  );
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpLocked, setOtpLocked] = useState(false);
  const [otpLockRemainingSeconds, setOtpLockRemainingSeconds] = useState(0);
  const [otpReady, setOtpReady] = useState(false);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState<ManualCartItem | null>(
    null,
  );
  const migrationDoneRef = useRef(false);
  const updateSeqRef = useRef<Map<string, number>>(new Map());
  const countryID = businessInfo?.countryID;

  const isValidBusinessInfo = useCallback(
    (value: BusinessInfo | null): value is BusinessInfo => {
      return Boolean(
        value &&
          typeof value.businessName === "string" &&
          value.businessName.trim() &&
          typeof value.haatBussID === "string" &&
          value.haatBussID.trim() &&
          typeof value.phone === "string" &&
          value.phone.trim(),
      );
    },
    [],
  );

  const applyItems = useCallback((next: ManualCartItem[]) => {
    const normalized = next.map(normalizeServerItem);
    cartService.setMemoryItems(normalized);
    setItems(normalized);
  }, []);

  const showCartError = useCallback((error?: unknown) => {
    console.error("Cart API error:", error);
    toast.error(translationService.translate("cart.syncError"));
  }, []);

  const migrateLegacyCartIfNeeded = useCallback(
    async (serverItems: ManualCartItem[]) => {
      if (migrationDoneRef.current) return serverItems;
      migrationDoneRef.current = true;

      const legacy = cartService.readLegacyLocalCart();
      if (legacy.length === 0) {
        cartService.clearLegacyLocalCart();
        return serverItems;
      }

      // Prefer server drafts when they already exist.
      if (serverItems.length > 0) {
        cartService.clearLegacyLocalCart();
        return serverItems;
      }

      for (const item of legacy) {
        try {
          if (item.type === "scanned" && item.product?.barcode) {
            await cartApiService.addScanned(
              {
                barcode: String(item.product.barcode).trim(),
                price: item.price || "",
                availability: item.availability === "FALSE" ? "FALSE" : "TRUE",
                notes: item.notes,
                avgWeight: item.product.avgWeight,
              },
              countryID,
            );
          } else if (item.type === "manual" && item.manualData?.name) {
            await cartApiService.addManual(
              {
                name: item.manualData.name,
                barcode: item.manualData.barcode,
                category: item.manualData.category,
                subCategory: item.manualData.subCategory,
                imageUrl: item.manualData.imageURL,
                quantityType: item.manualData.quantityType,
                avgWeight: item.manualData.avgWeight,
                price: item.price || "",
                availability: item.availability === "FALSE" ? "FALSE" : "TRUE",
                notes: item.notes,
              },
              countryID,
            );
          }
        } catch (error) {
          console.warn("Failed to migrate legacy cart item:", error);
        }
      }

      cartService.clearLegacyLocalCart();
      return cartApiService.listDraft(countryID);
    },
    [countryID],
  );

  const refreshCart = useCallback(async () => {
    if (!otpReady) {
      applyItems([]);
      return;
    }
    setIsCartLoading(true);
    try {
      let serverItems = await cartApiService.listDraft(countryID);
      serverItems = await migrateLegacyCartIfNeeded(serverItems);
      applyItems(serverItems);
    } catch (error) {
      showCartError(error);
    } finally {
      setIsCartLoading(false);
    }
  }, [
    applyItems,
    countryID,
    migrateLegacyCartIfNeeded,
    otpReady,
    showCartError,
  ]);

  // Auth bootstrap
  useEffect(() => {
    cartService.clearLegacyAuthSession();
    const savedBusinessInfo = cartService.getBusinessInfo();
    if (!isValidBusinessInfo(savedBusinessInfo)) {
      cartService.clearBusinessInfo();
      setBusinessInfoState(null);
      setOtpReady(false);
      setIsAuthInitializing(false);
      return;
    }

    let isMounted = true;
    authService
      .getProfile()
      .then(() => {
        if (!isMounted) return;
        setBusinessInfoState(savedBusinessInfo);
        setOtpReady(true);
      })
      .catch((error: { status?: number; statusCode?: number }) => {
        if (!isMounted) return;
        const status = error?.status ?? error?.statusCode;
        if (status === 401) {
          cartService.clearBusinessInfo();
          setBusinessInfoState(null);
          setOtpReady(false);
          return;
        }
        // Timeout, offline, or a server error: the cookie may still be valid.
        setBusinessInfoState(savedBusinessInfo);
        setOtpReady(true);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsAuthInitializing(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isValidBusinessInfo]);

  useEffect(() => {
    translationService.clampToCountry(businessInfo?.countryID);
  }, [businessInfo?.countryID]);

  useEffect(() => {
    if (!otpReady || isAuthInitializing) return;
    void refreshCart();
  }, [otpReady, isAuthInitializing, refreshCart]);

  const addScannedProduct = useCallback(
    async (
      product: Product,
      price: string,
      availability: "TRUE" | "FALSE",
      _rowIndex: number,
      notes?: string,
      catalogAvgWeight?: string,
    ): Promise<CartAddResult> => {
      const barcode = String(product.barcode || "").trim();
      const preflight = cartService.preflightAddByBarcode(barcode);
      if (preflight.status === "duplicate") {
        return { ...preflight.item, status: "duplicate", wasAdded: false };
      }
      if (preflight.status === "full") {
        return { status: "full", wasAdded: false };
      }

      try {
        const avgWeight =
          String(product.avgWeight || "").trim() || undefined;
        const response = await cartApiService.addScanned(
          {
            barcode,
            price,
            availability,
            notes,
            avgWeight,
          },
          countryID,
        );

        if (response.status === "full") {
          return { status: "full", wasAdded: false };
        }
        if (response.status === "duplicate" && response.item) {
          const item = normalizeServerItem(response.item);
          const current = cartService.getCartItems();
          applyItems([
            ...current.filter((i) => i.id !== item.id),
            item,
          ]);
          return { ...item, status: "duplicate", wasAdded: false };
        }

        const item = normalizeServerItem({
          ...(response.item as ManualCartItem),
          catalogAvgWeight:
            String(catalogAvgWeight || "").trim() ||
            (response.item as ManualCartItem)?.catalogAvgWeight,
        });
        const current = cartService.getCartItems();
        applyItems([...current.filter((i) => i.id !== item.id), item]);
        setLastAddedItem(item);
        return { ...item, status: "added", wasAdded: true };
      } catch (error) {
        showCartError(error);
        throw error;
      }
    },
    [applyItems, countryID, showCartError],
  );

  const addManualProduct = useCallback(
    async (
      data: {
        name: string;
        barcode?: string;
        category?: string;
        subCategory?: string;
        categoryNames?: { ar?: string; he?: string; en?: string };
        subCategoryNames?: { ar?: string; he?: string; en?: string };
        imageURL?: string;
        quantityType?: string;
        avgWeight?: string;
        weightToPresent?: string;
      },
      price: string,
      availability: "TRUE" | "FALSE",
      notes?: string,
    ): Promise<CartAddResult> => {
      const barcode = String(data.barcode || "").trim();
      if (barcode) {
        const preflight = cartService.preflightAddByBarcode(barcode);
        if (preflight.status === "duplicate") {
          return { ...preflight.item, status: "duplicate", wasAdded: false };
        }
      }
      if (cartService.getCartItems().length >= MAX_CART_ITEMS) {
        return { status: "full", wasAdded: false };
      }

      try {
        const response = await cartApiService.addManual(
          {
            name: data.name,
            barcode: barcode || undefined,
            category: data.category,
            subCategory: data.subCategory,
            imageUrl: data.imageURL,
            quantityType: data.quantityType,
            avgWeight: data.avgWeight,
            price,
            availability,
            notes,
          },
          countryID,
        );

        if (response.status === "full") {
          return { status: "full", wasAdded: false };
        }
        if (response.status === "duplicate" && response.item) {
          const item = normalizeServerItem(response.item);
          return { ...item, status: "duplicate", wasAdded: false };
        }

        const item = normalizeServerItem(response.item as ManualCartItem);
        // Preserve localized category name blobs for UI if server omits them.
        if (item.manualData) {
          item.manualData = {
            ...item.manualData,
            categoryNames: data.categoryNames || item.manualData.categoryNames,
            subCategoryNames:
              data.subCategoryNames || item.manualData.subCategoryNames,
          };
        }
        const current = cartService.getCartItems();
        applyItems([...current.filter((i) => i.id !== item.id), item]);
        setLastAddedItem(item);
        return { ...item, status: "added", wasAdded: true };
      } catch (error) {
        showCartError(error);
        throw error;
      }
    },
    [applyItems, countryID, showCartError],
  );

  const preflightAddByBarcode = useCallback(
    (barcode?: string) => cartService.preflightAddByBarcode(barcode),
    [],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      const previous = cartService.getCartItems();
      applyItems(previous.filter((item) => item.id !== itemId));
      try {
        await cartApiService.removeItem(itemId);
      } catch (error) {
        applyItems(previous);
        showCartError(error);
      }
    },
    [applyItems, showCartError],
  );

  const updateItem = useCallback(
    async (itemId: string, updates: CartItemUpdate) => {
      const seq = (updateSeqRef.current.get(itemId) ?? 0) + 1;
      updateSeqRef.current.set(itemId, seq);

      const previous = cartService.getCartItems();
      const next = previous.map((item) => {
        if (item.id !== itemId) return item;
        const { avgWeight, ...flat } = updates;
        let updated: ManualCartItem = { ...item, ...flat };
        if (avgWeight !== undefined) {
          if (updated.product) {
            updated = {
              ...updated,
              product: { ...updated.product, avgWeight },
            };
          } else if (updated.manualData) {
            updated = {
              ...updated,
              manualData: { ...updated.manualData, avgWeight },
            };
          }
        }
        return updated;
      });
      applyItems(next);

      try {
        const saved = await cartApiService.updateItem(
          itemId,
          updates,
          countryID,
        );
        // A newer keystroke already updated this item. Drop the stale save.
        if (updateSeqRef.current.get(itemId) !== seq) return;
        const after = cartService.getCartItems();
        applyItems(
          after.map((item) =>
            item.id === itemId ? normalizeServerItem(saved) : item,
          ),
        );
      } catch (error) {
        if (updateSeqRef.current.get(itemId) !== seq) return;
        applyItems(previous);
        showCartError(error);
      }
    },
    [applyItems, countryID, showCartError],
  );

  const clearCart = useCallback(async () => {
    const previous = cartService.getCartItems();
    applyItems([]);
    try {
      await cartApiService.clearDrafts();
    } catch (error) {
      applyItems(previous);
      showCartError(error);
    }
  }, [applyItems, showCartError]);

  const submitCart = useCallback(async () => {
    if (!businessInfo) {
      return {
        ok: false,
        message: "Missing business info",
        count: 0,
        duplicateSkippedCount: 0,
      };
    }
    const result = await cartApiService.submit({
      businessName: businessInfo.businessName,
      phone: businessInfo.phone,
      countryID: businessInfo.countryID,
    });
    await refreshCart();
    return result;
  }, [businessInfo, refreshCart]);

  const setBusinessInfo = useCallback((info: BusinessInfo) => {
    cartService.saveBusinessInfo(info);
    setBusinessInfoState(info);
  }, []);

  const clearBusinessInfo = useCallback(() => {
    cartService.clearBusinessInfo();
    setBusinessInfoState(null);
  }, []);

  const completeOtpAuthentication = useCallback((info: BusinessInfo) => {
    translationService.clampToCountry(info.countryID);
    cartService.saveBusinessInfo(info);
    setBusinessInfoState(info);
    setOtpSending(false);
    setOtpVerifying(false);
    setOtpLocked(false);
    setOtpLockRemainingSeconds(0);
    setOtpReady(true);
    migrationDoneRef.current = false;
  }, []);

  const logoutOtpSession = useCallback(() => {
    cartService.clearBusinessInfo();
    setBusinessInfoState(null);
    setOtpSending(false);
    setOtpVerifying(false);
    setOtpLocked(false);
    setOtpLockRemainingSeconds(0);
    setOtpReady(false);
    applyItems([]);
    migrationDoneRef.current = false;
  }, [applyItems]);

  const setOtpLockedState = useCallback(
    (locked: boolean, lockRemainingSeconds: number = 0) => {
      setOtpLocked(locked);
      setOtpLockRemainingSeconds(Math.max(0, lockRemainingSeconds));
    },
    [],
  );

  const clearLastAddedItem = useCallback(() => {
    setLastAddedItem(null);
  }, []);

  const value: CartContextType = {
    items,
    itemCount: items.length,
    maxCartItems: MAX_CART_ITEMS,
    remainingCartSlots: Math.max(0, MAX_CART_ITEMS - items.length),
    isCartFull: items.length >= MAX_CART_ITEMS,
    isCartLoading,
    addScannedProduct,
    addManualProduct,
    preflightAddByBarcode,
    removeItem,
    updateItem,
    clearCart,
    refreshCart,
    submitCart,
    getItemName: cartService.getItemName.bind(cartService),
    getItemImage: cartService.getItemImage.bind(cartService),
    getItemBarcode: cartService.getItemBarcode.bind(cartService),
    businessInfo,
    setBusinessInfo,
    clearBusinessInfo,
    otpSending,
    otpVerifying,
    otpLocked,
    otpReady,
    isAuthInitializing,
    otpLockRemainingSeconds,
    setOtpSending,
    setOtpVerifying,
    setOtpLockedState,
    completeOtpAuthentication,
    logoutOtpSession,
    lastAddedItem,
    clearLastAddedItem,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
